import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import {
  CapitalizePipe,
  DisabledDirective,
  DeleteDialogComponent,
  DialogResult,
  DocumentLinksComponent,
  DocumentTagsService,
  errorToast,
  FileUploadComponent,
  FormErrorService,
  Media,
  MediaService,
  RcMultiSelectWrapPaddingDirective,
  RcPanelBarNoCollapseDirective,
  replaceUndefinedByNull,
  successSaveToast,
  SupplierCreditNote,
  SupplierCreditNoteFee,
  SupplierCreditNoteFeeService,
  SupplierCreditNoteService,
  SupplierCreditNoteStatus,
  UserNotificationService,
  ValidationDirective,
  DropdownLoaderDirective,
} from 'shared';
import {
  PanelBarComponent,
  PanelBarContentDirective,
  PanelBarExpandMode,
  PanelBarItemComponent,
} from '@progress/kendo-angular-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormFieldComponent, SwitchComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { DatePipe, NumberFormatOptions } from '@progress/kendo-angular-intl';
import { debounceTime, Observable, of, Subject, take } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { AsyncPipe } from '@angular/common';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import {
  SupplierCreditNoteFeePopupComponent
} from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/modals/supplier-credit-note-fee-popup/supplier-credit-note-fee-popup.component';
import { DocumentFeeCardComponent } from '@features/fee/document-fee-card/document-fee-card.component';

@Component({
  selector: 'rc-supplier-credit-note-sidebar-panel',
  standalone: true,
  imports: [
    DocumentFeeCardComponent,
    PanelBarComponent,
    PanelBarItemComponent,
    CapitalizePipe,
    TranslateModule,
    PanelBarContentDirective,
    FormFieldComponent,
    LabelComponent,
    DatePipe,
    SwitchComponent,
    FileUploadComponent,
    ReactiveFormsModule,
    MultiSelectComponent,
    ValidationDirective,
    RcMultiSelectWrapPaddingDirective,
    RcPanelBarNoCollapseDirective,
    AsyncPipe,
    DisabledDirective,
    LoaderComponent,
    DocumentLinksComponent,
    DropdownLoaderDirective,
  ],
  providers: [CapitalizePipe, TranslateModule, FormErrorService, DialogRef, UserNotificationService],
  templateUrl: './supplier-credit-note-sidebar-panel.component.html',
  styleUrl: './supplier-credit-note-sidebar-panel.component.scss',
})
export class SupplierCreditNoteSidebarPanelComponent {
  #mediaService = inject(MediaService);
  #capitalizePipe = inject(CapitalizePipe);
  #translateService = inject(TranslateService);
  #formErrorService = inject(FormErrorService);
  #notificationService = inject(NotificationService);
  #supplierCreditNoteService = inject(SupplierCreditNoteService);
  #supplierCreditNoteFeeService = inject(SupplierCreditNoteFeeService);
  #documentTagService = inject(DocumentTagsService);
  #dialogService = inject(DialogService);
  #dialog = inject(DialogRef);

  supplierCreditNote = input<SupplierCreditNote>();
  isDoNotCreditSwitchLocked = computed(() => this.supplierCreditNote()!.status === SupplierCreditNoteStatus.PROCESSED);
  isSupplierCreditNoteLocked = computed(() => this.supplierCreditNote()!.status === SupplierCreditNoteStatus.PROCESSED);
  readonly = input<boolean>(false);
  loading = signal<boolean>(false);

  updateTotals = output<boolean>();

  supplierCreditNoteForm: FormGroup<SupplierCreditNoteForm> = this.#initForm();
  supplierCreditNoteAttachmentsForm: FormGroup<SupplierCreditNoteAttachmentsForm> = this.#initAttachmentsForm();

  files$: Observable<Media[]> = of<Media[]>([]);
  extraCosts$: Observable<SupplierCreditNoteFee[]> = of([]);
  tags$ = this.#documentTagService.getDocumentTags();
  updateSubject$ = new Subject<boolean>();
  #tagSubject$ = new Subject<string>();

  expandMode: PanelBarExpandMode = 1;
  uploadUrl = 'api/supplier_credit_note_medias';
  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
  };

  constructor() {
    effect(() => {
      const scn = this.supplierCreditNote();
      if (!scn) return;

      this.files$ = this.#mediaService.getAllMedias(`${scn['@id']}/medias`);
      this.extraCosts$ = this.#supplierCreditNoteFeeService.getForSupplierCreditNote(scn['@id']!);
      this.#patchForm(scn);
      this.#getFiles();
    });

    this.updateSubject$.pipe(debounceTime(600)).subscribe(() => {
      this.#updateSupplierCreditNote();
    });

    this.#tagSubject$.pipe(debounceTime(300)).subscribe((searchTerm) => {
      this.tags$ = this.#documentTagService.findDocumentTagsByName(searchTerm);
    });
  }

  updateForm() {
    this.updateSubject$.next(true);
  }

  markAsDirty(update: { status: boolean; attachments: Media[] }) {
    const media = update.attachments.map((att) => ({ ...att, supplierInvoice: this.supplierCreditNote()!['@id']! }));
    this.supplierCreditNoteAttachmentsForm.patchValue({
      media: media,
    });
    this.onSubmit();
  }

  onSubmit() {
    let action;
    replaceUndefinedByNull(this.supplierCreditNoteAttachmentsForm.value);
    const files = this.supplierCreditNoteAttachmentsForm.value.media ?? [];
    this.supplierCreditNoteAttachmentsForm.patchValue({
      media: files.map((value) => value['@id']) as unknown as Media[],
    });
    action = this.#supplierCreditNoteService.update(
      this.supplierCreditNote()!['@id']!,
      this.supplierCreditNoteAttachmentsForm.value as unknown as SupplierCreditNote,
    );
    action.pipe(take(1), distinctUntilChanged()).subscribe({
      next: (response: SupplierCreditNote) => {
        this.files$ = this.#mediaService.getAllMedias(`${response['@id']}/medias`);
        this.#getFiles();
        const notification = this.#notificationService.show(successSaveToast);
        if (notification.content) {
          const si = this.#capitalizePipe.transform(this.#translateService.instant('SUPPLIER_CREDIT_NOTE'));
          notification.content.instance.value = `${si}: ${this.supplierCreditNote()?.documentNumber}`;
        }
      },
      error: (err) => {
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) {
          notification.content.instance.setCurrentValue(err);
        }
        this.#formErrorService.setErrorViolations(err);
      },
    });
  }

  updateTags(event: string[]) {
    this.supplierCreditNoteForm.patchValue({ tags: event });
    this.updateSubject$.next(true);
  }

  filterTags(searchTerm: string) {
    this.#tagSubject$.next(searchTerm);
  }

  openCreateExtraCostDialog() {
    const title = 'CREATE_DIALOG_TITLE';
    this.#dialog = this.#dialogService.open({
      content: SupplierCreditNoteFeePopupComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant(title, { value: this.#translateService.instant('EXTRA_COST') }),
      ),
    });
    this.#dialog.content.instance.supplierCreditNote.set(this.supplierCreditNote()!['@id']);
    this.#dialog.result.subscribe(() => {
      this.updateTotals.emit(true);
    });
  }

  openDeleteExtraCostDialog(fee: SupplierCreditNoteFee) {
    const title = 'DELETE_DIALOG_TITLE';
    this.#dialog = this.#dialogService.open({
      content: DeleteDialogComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant(title, { value: this.#translateService.instant('EXTRA_COST') }),
      ),
    });

    this.#dialog.content.instance.value = `${fee.description}: ${fee.amount}`;
    this.#dialog.result.subscribe((value: DialogResult) => {
      if (value.delete) {
        this.#deleteExtraCost(fee);
      }
    });
  }

  #deleteExtraCost(fee: SupplierCreditNoteFee) {
    this.#supplierCreditNoteFeeService.delete(fee['@id']!).subscribe(() => {
      this.updateTotals.emit(true);
    });
  }

  #updateSupplierCreditNote() {
    this.#supplierCreditNoteService
      .update(this.supplierCreditNote()!['@id']!, this.supplierCreditNoteForm.value as unknown as SupplierCreditNote)
      .subscribe({
        next: (response: SupplierCreditNote) => {
          this.supplierCreditNoteForm.patchValue(response);
          const notification = this.#notificationService.show(successSaveToast);
          if (notification.content) notification.content.instance.value = 'SUPPLIER_CREDIT_NOTE';
          this.updateTotals.emit(true);
        },
        error: (err: HttpErrorResponse) => {
          const notification = this.#notificationService.show(errorToast);
          if (notification.content) {
            notification.content.instance.setCurrentValue(err);
          }
          this.#formErrorService.setErrorViolations(err);
        },
      });
  }

  #initForm() {
    const form = new FormGroup<SupplierCreditNoteForm>({
      doNotCredit: new FormControl(false, {
        nonNullable: true,
        validators: [],
      }),
      tags: new FormControl([], {
        nonNullable: true,
        validators: [],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  #initAttachmentsForm() {
    const form = new FormGroup<SupplierCreditNoteAttachmentsForm>({
      media: new FormControl([], {
        nonNullable: true,
        validators: [],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  #patchForm(supplierCreditNote: SupplierCreditNote) {
    this.supplierCreditNoteAttachmentsForm.patchValue({
      media: supplierCreditNote.media as unknown as Media[],
    });

    this.supplierCreditNoteForm.patchValue({
      doNotCredit: supplierCreditNote.doNotCredit,
      tags: supplierCreditNote.tags ?? [],
    });
  }

  #getFiles() {
    this.loading.set(true);
    this.files$.subscribe((media) => {
      this.supplierCreditNoteAttachmentsForm.patchValue({
        media: media,
      });
      this.loading.set(false);
    });
  }

  protected readonly SupplierCreditNoteStatus = SupplierCreditNoteStatus;
}

interface SupplierCreditNoteForm {
  doNotCredit: FormControl<boolean>;
  tags: FormControl<string[]>;
}

interface SupplierCreditNoteAttachmentsForm {
  media: FormControl<Media[]>;
}
